import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PublicLayout from '../components/PublicLayout';
import { BookOpen, Calendar, User, ChevronLeft, ChevronRight } from 'lucide-react';

const MagazineCarousel = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  
  console.log('MagazineCarousel component mounted! Current slide:', currentSlide);
  
  const slides = [
    {
      icon: BookOpen,
      title: "Industry Insights",
      description: "Expert analysis of current trends and future predictions in business and entrepreneurship"
    },
    {
      icon: User,
      title: "Member Spotlights",
      description: "Success stories from our community members and their journey to excellence"
    },
    {
      icon: Calendar,
      title: "Upcoming Events",
      description: "Preview of exclusive events and opportunities available to Success Club members"
    }
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const CurrentIcon = slides[currentSlide].icon;

  return (
    <div className="relative">
      {/* Carousel Content */}
      <div className="text-center min-h-[300px] flex flex-col items-center justify-center px-8">
        <div className="w-20 h-20 rounded-full mx-auto mb-6" style={{background: 'var(--gold)', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
          <CurrentIcon className="w-10 h-10" style={{color: '#fff'}} />
        </div>
        <h3 style={{fontFamily: 'var(--font-heading)', fontSize: '2rem', marginBottom: '1rem'}}>
          {slides[currentSlide].title}
        </h3>
        <p style={{color: 'var(--muted)', fontSize: '1.125rem', maxWidth: '600px', lineHeight: '1.7'}}>
          {slides[currentSlide].description}
        </p>
      </div>

      {/* Navigation Arrows */}
      <button
        onClick={prevSlide}
        className="absolute left-0 top-1/2 -translate-y-1/2 p-2 rounded-full hover:opacity-80 transition-opacity"
        style={{background: 'var(--gold)', color: '#fff'}}
      >
        <ChevronLeft className="w-6 h-6" />
      </button>
      <button
        onClick={nextSlide}
        className="absolute right-0 top-1/2 -translate-y-1/2 p-2 rounded-full hover:opacity-80 transition-opacity"
        style={{background: 'var(--gold)', color: '#fff'}}
      >
        <ChevronRight className="w-6 h-6" />
      </button>

      {/* Dots Navigation */}
      <div className="flex items-center justify-center gap-3 mt-8">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className="transition-all duration-300 rounded-full"
            style={{
              width: currentSlide === index ? '32px' : '12px',
              height: '12px',
              background: currentSlide === index ? 'var(--gold)' : 'rgba(0,0,0,0.2)',
            }}
          />
        ))}
      </div>
    </div>
  );
};

const MagazinePage = () => {
  const navigate = useNavigate();

  const latestIssues = [
    {
      title: "The Future of Entrepreneurship",
      issue: "March 2025",
      author: "Editorial Team",
      description: "Discover emerging trends and strategies shaping the future of business leadership.",
      coverImage: "https://images.unsplash.com/photo-1556761175-b413da4baf72?w=400&h=500&fit=crop"
    },
    {
      title: "Building Resilient Teams",
      issue: "February 2025",
      author: "Sarah Johnson",
      description: "Expert insights on creating and maintaining high-performing teams in challenging times.",
      coverImage: "https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=400&h=500&fit=crop"
    },
    {
      title: "Digital Transformation Success Stories",
      issue: "January 2025",
      author: "Michael Chen",
      description: "Real-world case studies of businesses that successfully navigated digital transformation.",
      coverImage: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&h=500&fit=crop"
    }
  ];

  return (
    <PublicLayout>
      <div className="public-page">
        {/* Hero Section */}
        <div className="public-page-header">
          <h1>Digital Magazine</h1>
          <p>
            Monthly insights, member spotlights, and industry trends curated for ambitious leaders.
          </p>
        </div>

        <div className="public-page-content">
          {/* Magazine Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {latestIssues.map((issue, index) => (
              <div key={index} className="dashboard-card">
                <div className="aspect-square rounded-lg overflow-hidden mb-4" style={{maxWidth: '250px', margin: '0 auto 1rem'}}>
                  <img 
                    src={issue.coverImage} 
                    alt={issue.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                
                <div className="flex items-center gap-4 mb-3">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" style={{color: 'var(--gold)'}} />
                    <span className="text-sm" style={{color: 'var(--muted)'}}>{issue.issue}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4" style={{color: 'var(--gold)'}} />
                    <span className="text-sm" style={{color: 'var(--muted)'}}>{issue.author}</span>
                  </div>
                </div>
                
                <h3 style={{fontFamily: 'var(--font-heading)', fontSize: '1.5rem', marginBottom: '0.75rem'}}>{issue.title}</h3>
                <p style={{color: 'var(--muted)', marginBottom: '1.5rem'}}>{issue.description}</p>
                
                <button 
                  onClick={() => navigate('/auth')}
                  className="btn-outline w-full"
                >
                  Read Issue
                </button>
              </div>
            ))}
          </div>

          {/* Features Section - Interactive Carousel */}
          <div className="rounded-2xl p-12 mb-16" style={{background: 'var(--cream)', border: '1px solid rgba(0,0,0,0.08)'}}>
            <div className="text-center mb-12">
              <h2 style={{fontFamily: 'var(--font-heading)', fontSize: '3rem', marginBottom: '1rem'}}>What's Inside</h2>
              <p style={{color: 'var(--muted)', fontSize: '1.125rem'}}>Every issue is packed with valuable content</p>
            </div>
            
            <MagazineCarousel />
          </div>

          {/* Advertisement Options */}
          <div className="mb-16">
            <div className="text-center mb-12">
              <h2 style={{fontFamily: 'var(--font-heading)', fontSize: '3rem', marginBottom: '1rem'}}>Advertise With Us</h2>
              <p style={{color: 'var(--muted)', fontSize: '1.125rem', maxWidth: '700px', margin: '0 auto'}}>
                Reach ambitious leaders and entrepreneurs with premium advertising opportunities in our digital magazine
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
              {/* Full-Page Ad */}
              <div className="dashboard-card text-center" style={{display: 'flex', flexDirection: 'column', height: '100%'}}>
                <div className="mb-6">
                  <div className="inline-block px-4 py-1.5 rounded mb-4" style={{background: 'var(--gold)', color: '#000', fontSize: '0.875rem', fontWeight: '600'}}>
                    PREMIUM
                  </div>
                  <h3 style={{fontFamily: 'var(--font-heading)', fontSize: '1.75rem', marginBottom: '0.5rem'}}>Full-Page Ad</h3>
                  <p style={{color: 'var(--muted)', fontSize: '0.875rem', marginBottom: '1rem'}}>8.5" × 11"</p>
                  <div className="text-4xl font-bold mb-2" style={{color: 'var(--gold)'}}>$500</div>
                  <p style={{color: 'var(--muted)', fontSize: '0.875rem'}}>per issue</p>
                </div>
                <div className="mb-6 text-left" style={{flexGrow: 1}}>
                  <p style={{color: 'var(--muted)', fontSize: '0.875rem', lineHeight: '1.6'}}>
                    Maximum visibility with a full-page spread. Perfect for major announcements and brand awareness campaigns.
                  </p>
                </div>
                <a 
                  href="mailto:info@oursuccessclub.com?subject=Full-Page Ad Inquiry" 
                  className="btn-primary block text-center no-underline"
                  style={{marginTop: 'auto'}}
                >
                  Purchase Ad
                </a>
              </div>

              {/* Half-Page Ad */}
              <div className="dashboard-card text-center" style={{display: 'flex', flexDirection: 'column', height: '100%'}}>
                <div className="mb-6">
                  <h3 style={{fontFamily: 'var(--font-heading)', fontSize: '1.75rem', marginBottom: '0.5rem'}}>Half-Page Ad</h3>
                  <p style={{color: 'var(--muted)', fontSize: '0.875rem', marginBottom: '1rem'}}>8.5" × 5.5"</p>
                  <div className="text-4xl font-bold mb-2" style={{color: 'var(--gold)'}}>$300</div>
                  <p style={{color: 'var(--muted)', fontSize: '0.875rem'}}>per issue</p>
                </div>
                <div className="mb-6 text-left" style={{flexGrow: 1}}>
                  <p style={{color: 'var(--muted)', fontSize: '0.875rem', lineHeight: '1.6'}}>
                    Great balance of visibility and value. Ideal for product launches and service promotions.
                  </p>
                </div>
                <a 
                  href="mailto:info@oursuccessclub.com?subject=Half-Page Ad Inquiry" 
                  className="btn-primary block text-center no-underline"
                  style={{marginTop: 'auto'}}
                >
                  Purchase Ad
                </a>
              </div>

              {/* Quarter-Page Ad */}
              <div className="dashboard-card text-center" style={{display: 'flex', flexDirection: 'column', height: '100%'}}>
                <div className="mb-6">
                  <h3 style={{fontFamily: 'var(--font-heading)', fontSize: '1.75rem', marginBottom: '0.5rem'}}>Quarter-Page Ad</h3>
                  <p style={{color: 'var(--muted)', fontSize: '0.875rem', marginBottom: '1rem'}}>4.25" × 5.5"</p>
                  <div className="text-4xl font-bold mb-2" style={{color: 'var(--gold)'}}>$175</div>
                  <p style={{color: 'var(--muted)', fontSize: '0.875rem'}}>per issue</p>
                </div>
                <div className="mb-6 text-left" style={{flexGrow: 1}}>
                  <p style={{color: 'var(--muted)', fontSize: '0.875rem', lineHeight: '1.6'}}>
                    Cost-effective option with strong impact. Perfect for targeted messaging and special offers.
                  </p>
                </div>
                <a 
                  href="mailto:info@oursuccessclub.com?subject=Quarter-Page Ad Inquiry" 
                  className="btn-primary block text-center no-underline"
                  style={{marginTop: 'auto'}}
                >
                  Purchase Ad
                </a>
              </div>

              {/* Business Card Ad */}
              <div className="dashboard-card text-center" style={{display: 'flex', flexDirection: 'column', height: '100%'}}>
                <div className="mb-6">
                  <h3 style={{fontFamily: 'var(--font-heading)', fontSize: '1.75rem', marginBottom: '0.5rem'}}>Business Card Ad</h3>
                  <p style={{color: 'var(--muted)', fontSize: '0.875rem', marginBottom: '1rem'}}>3.5" × 2"</p>
                  <div className="text-4xl font-bold mb-2" style={{color: 'var(--gold)'}}>$75</div>
                  <p style={{color: 'var(--muted)', fontSize: '0.875rem'}}>per issue</p>
                </div>
                <div className="mb-6 text-left" style={{flexGrow: 1}}>
                  <p style={{color: 'var(--muted)', fontSize: '0.875rem', lineHeight: '1.6'}}>
                    Affordable entry point for brand presence. Ideal for contact information and quick promotions.
                  </p>
                </div>
                <a 
                  href="mailto:info@oursuccessclub.com?subject=Business Card Ad Inquiry" 
                  className="btn-primary block text-center no-underline"
                  style={{marginTop: 'auto'}}
                >
                  Purchase Ad
                </a>
              </div>
            </div>

            {/* Ad Guidelines */}
            <div className="mt-12 max-w-4xl mx-auto rounded-xl p-8" style={{background: 'var(--cream)', border: '1px solid rgba(0,0,0,0.08)'}}>
              <h3 style={{fontFamily: 'var(--font-heading)', fontSize: '1.5rem', marginBottom: '1rem', textAlign: 'center'}}>Ad Specifications & Guidelines</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold mb-2" style={{color: 'var(--gold)'}}>File Requirements:</h4>
                  <ul className="space-y-1 text-sm" style={{color: 'var(--muted)'}}>
                    <li>• High-resolution PDF or JPEG (300 DPI minimum)</li>
                    <li>• CMYK color mode preferred</li>
                    <li>• 0.125" bleed on all sides</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold mb-2" style={{color: 'var(--gold)'}}>Submission Deadline:</h4>
                  <ul className="space-y-1 text-sm" style={{color: 'var(--muted)'}}>
                    <li>• 2 weeks before publication date</li>
                    <li>• Artwork approval within 3 business days</li>
                    <li>• Contact us for multi-issue discounts</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="text-center rounded-2xl p-12" style={{background: 'linear-gradient(135deg, #D4AF37 0%, #C29A4B 100%)', color: '#fff'}}>
            <h2 className="mb-4" style={{color: '#fff', fontFamily: 'var(--font-heading)', fontSize: '3rem'}}>Get Full Access</h2>
            <p className="text-xl mb-8 max-w-2xl mx-auto" style={{color: 'rgba(255,255,255,0.9)'}}>
              Join Success Club to read all issues and exclusive content.
            </p>
            <button 
              onClick={() => navigate('/auth')}
              className="bg-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition-colors"
              style={{color: '#1A1A1A'}}
            >
              Join Now
            </button>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
};

export default MagazinePage;